/**
 * FavoriteButton Component
 * Allows authenticated users to save items to their favorites
 */

'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { db } from '@/lib/firebase.client';
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';

interface FavoriteButtonProps {
  itemId: string;
  title: string;
  description?: string;
  image?: string;
  classification?: string;
  productUrl?: string;
  className?: string;
  showLabel?: boolean;
}

export default function FavoriteButton({
  itemId,
  title,
  description,
  image,
  classification,
  productUrl,
  className = '',
  showLabel = false,
}: FavoriteButtonProps) {
  const { user } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [favoriteDocId, setFavoriteDocId] = useState<string | null>(null);

  // Check if item is already favorited
  useEffect(() => {
    if (!user) {
      setIsFavorited(false);
      return;
    }

    const checkFavorite = async () => {
      try {
        const favoritesRef = collection(db, 'users', user.uid, 'favorites');
        const q = query(favoritesRef, where('itemId', '==', itemId));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          setIsFavorited(true);
          setFavoriteDocId(snapshot.docs[0].id);
        } else {
          setIsFavorited(false);
          setFavoriteDocId(null);
        }
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    checkFavorite();
  }, [user, itemId]);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      // Redirect to sign in or show modal
      window.location.href = '/auth/signin?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }

    setLoading(true);

    try {
      if (isFavorited && favoriteDocId) {
        // Remove from favorites
        await deleteDoc(doc(db, 'users', user.uid, 'favorites', favoriteDocId));
        setIsFavorited(false);
        setFavoriteDocId(null);
      } else {
        // Add to favorites
        const newFavoriteRef = doc(collection(db, 'users', user.uid, 'favorites'));
        await setDoc(newFavoriteRef, {
          itemId,
          title,
          description: description || '',
          image: image || '',
          classification: classification || '',
          productUrl: productUrl || '',
          addedAt: new Date(),
        });
        setIsFavorited(true);
        setFavoriteDocId(newFavoriteRef.id);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={toggleFavorite}
      disabled={loading}
      className={`inline-flex items-center gap-2 transition-all duration-200 ${
        isFavorited
          ? 'text-red-500 hover:text-red-600'
          : 'text-gray-400 hover:text-red-500'
      } disabled:opacity-50 ${className}`}
      title={user ? (isFavorited ? 'Remove from favorites' : 'Add to favorites') : 'Sign in to save favorites'}
      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <svg
        className={`w-6 h-6 transition-transform duration-200 ${loading ? 'animate-pulse' : ''} ${isFavorited ? 'scale-110' : ''}`}
        fill={isFavorited ? 'currentColor' : 'none'}
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
      {showLabel && (
        <span className="text-sm font-medium">
          {isFavorited ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
}

